import { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useToast } from "@/components/ui/use-toast";
import { CheckCircle, Eye, EyeOff, AlertCircle, XCircle } from "lucide-react";
import { PhoneInput } from "@/components/ui/phone-input";
import { CountrySelect, countries } from "@/components/ui/country-select";
import { LysaraLogo } from "@/components/ui/lysara-logo";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useOutletLogin } from "@/contexts/OutletLoginContext";

const AUTH_PATHS = ['/auth','/outlet-auth','/outlet-user-auth','/factory-auth','/factory-user-auth','/change-password','/setup-password','/verify','/accept-invite'];

function safeInternalPath(p: unknown): string | null {
  if (typeof p !== 'string' || p.length === 0) return null;
  if (p[0] !== '/') return null;
  if (p.startsWith('//') || p.startsWith('/\\')) return null;
  if (/\\/.test(p)) return null;
  const firstSegment = p.split(/[/?#]/)[1] ?? '';
  if (firstSegment.includes(':')) return null;
  if (AUTH_PATHS.some(a => p === a || p.startsWith(a + '/') || p.startsWith(a + '?') || p.startsWith(a + '#'))) return null;
  return p;
}

export default function Auth() {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const outletLogin = useOutletLogin();
  const clearOutletContext = outletLogin?.clearOutletContext || (() => {});

  // Check for returning user query params
  const searchParams = new URLSearchParams(location.search);
  const isReturningUser = searchParams.get('returning_user') === 'true';
  const tenantName = searchParams.get('tenant_name');
  const prefilledEmail = searchParams.get('email');
  const errorType = searchParams.get('error');
  
  // State for persistent error from sessionStorage
  const [persistentError, setPersistentError] = useState<any>(null);

  // Multi-field login state
  const [companyId, setCompanyId] = useState("");
  const [employeeId, setEmployeeId] = useState("");
  const [password, setPassword] = useState("");
  
  // Legacy email login state
  const [email, setEmail] = useState("");
  
  // Sign up state - Personal Information
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phoneCountryCode, setPhoneCountryCode] = useState("+65");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const companyIdInputRef = useRef<HTMLInputElement>(null);
  
  // Forgot password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  
  // Sign up state - Business Information
  const [businessName, setBusinessName] = useState("");
  const [legalName, setLegalName] = useState("");
  const [businessType, setBusinessType] = useState("");
  const [businessCountry, setBusinessCountry] = useState("");
  const [currency, setCurrency] = useState("");
  const [subdomain, setSubdomain] = useState("");
  const [subdomainAvailable, setSubdomainAvailable] = useState<boolean | null>(null);
  const [subdomainError, setSubdomainError] = useState("");
  
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState<"signin" | "signup">("signin");
  const [resendEmail, setResendEmail] = useState("");
  const [resendLoading, setResendLoading] = useState(false);
  
  // Check for persistent error from sessionStorage on mount
  useEffect(() => {
    const storedError = sessionStorage.getItem('device_auth_error');
    if (storedError) {
      try {
        const errorData = JSON.parse(storedError);
        // Check if error is not too old (10 minutes)
        if (Date.now() - errorData.timestamp < 600000) {
          setPersistentError(errorData);
        } else {
          sessionStorage.removeItem('device_auth_error');
        }
      } catch (e) {
        sessionStorage.removeItem('device_auth_error');
      }
    }
  }, []);
  
  const clearPersistentError = () => {
    setPersistentError(null);
    sessionStorage.removeItem('device_auth_error');
  };

  // Handle auth state changes and redirect when signed in
  useEffect(() => {
    let isRedirecting = false;

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        // CRITICAL SECURITY: Do NOT auto-redirect for password recovery sessions
        // Recovery sessions must go through /change-password first
        if (event === 'PASSWORD_RECOVERY') {
          console.log('[Auth] Password recovery detected, redirecting to change-password');
          navigate('/change-password', { replace: true });
          return;
        }
        
        if (event === 'SIGNED_IN' && session && !isRedirecting) {
          // Form sign-in validates its requested tenant after the Auth call
          // resolves. Do not issue additional Supabase calls in this callback.
          if (sessionStorage.getItem('company_login_pending') === 'true') {
            return;
          }
          // CRITICAL: Wait briefly to allow other tabs to set recovery flag via localStorage
          await new Promise(resolve => setTimeout(resolve, 100));
          
          // Check localStorage recovery flag (cross-tab persistence)
          const isRecoveryFromStorage = localStorage.getItem('recovery_session_active') === 'true';
          if (isRecoveryFromStorage) {
            console.log('[Auth] Recovery session detected from localStorage, redirecting to change-password');
            navigate('/change-password', { replace: true });
            return;
          }
          
          // Also decode JWT to check AMR for recovery sessions
          try {
            const payload = JSON.parse(atob(session.access_token.split('.')[1]));
            const amr = payload.amr as Array<{ method: string }> | undefined;
            if (amr?.some(entry => entry.method === 'recovery')) {
              console.log('[Auth] Recovery session detected from JWT AMR, redirecting to change-password');
              localStorage.setItem('recovery_session_active', 'true');
              navigate('/change-password', { replace: true });
              return;
            }
          } catch (e) {
            console.warn('[Auth] Failed to decode JWT for AMR check:', e);
          }
          
          const { data: appUser, error: appUserError } = await supabase
            .from("app_users")
            .select("is_super_admin")
            .eq("id", session.user.id)
            .maybeSingle();

          if (appUserError) {
            console.error("[Auth] Failed to determine account access level:", appUserError);
          }

          isRedirecting = true;
          const requestedTenantSlug = sessionStorage.getItem('requested_tenant_slug');
          if (requestedTenantSlug && !appUser?.is_super_admin) {
            const { data: memberships, error: membershipError } = await supabase
              .from('memberships')
              .select('status, tenant:tenants!inner(slug, status)')
              .eq('user_id', session.user.id)
              .eq('status', 'active');

            const hasRequestedTenant = !membershipError && (memberships || []).some(
              (membership: any) =>
                membership.tenant?.slug === requestedTenantSlug &&
                membership.tenant?.status === 'active',
            );

            if (!hasRequestedTenant) {
              sessionStorage.removeItem('requested_tenant_slug');
              localStorage.removeItem('selectedTenantId');
              localStorage.removeItem('selectedTenantSlug');
              setLoading(false);
              await supabase.auth.signOut({ scope: 'local' });
              toast({ title: 'Sign in failed', description: 'Invalid Company ID, email, or password.', variant: 'destructive' });
              return;
            }

            localStorage.setItem('selectedTenantSlug', requestedTenantSlug);
            sessionStorage.removeItem('requested_tenant_slug');
          }

          console.log("[Auth] Sign in detected, redirecting to the appropriate workspace");

          // Redirect to preserved internal path if safe, else /dashboard.
          const fromState = (location.state as any)?.from;
          const candidate =
            typeof fromState === 'string'
              ? fromState
              : fromState && typeof fromState === 'object' && typeof fromState.pathname === 'string'
                ? `${fromState.pathname}${fromState.search ?? ''}${fromState.hash ?? ''}`
                : null;
          const target = appUser?.is_super_admin
            ? '/superadmin'
            : safeInternalPath(candidate) ?? '/dashboard';
          navigate(target, { replace: true });
        }
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, [navigate]);

  // Prefill email for returning users
  useEffect(() => {
    if (isReturningUser && prefilledEmail) {
      setEmployeeId(prefilledEmail);
    }
  }, [isReturningUser, prefilledEmail]);

  // Use current origin so links work in sandbox and production
  const redirectTo = `${window.location.origin}/dashboard`;
  
  // Normalize tenant slug input (strip quotes/invalid chars)
  const sanitizeSlugInput = (value: string) =>
    value
      .toLowerCase()
      .trim()
      .replace(/^"+|"+$/g, "")
      .replace(/[^a-z0-9-]/g, "");

  const validateSubdomain = (value: string) => {
    const slug = value.toLowerCase().trim();
    
    // Basic format validation
    if (slug.length < 3 || slug.length > 30) {
      return "Subdomain must be 3-30 characters long";
    }
    
    if (!/^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/.test(slug)) {
      return "Only lowercase letters, numbers, and hyphens allowed. Must start and end with alphanumeric characters";
    }
    
    // Reserved words
    const reserved = ['admin', 'api', 'www', 'app', 'mail', 'ftp', 'localhost', 'lysara'];
    if (reserved.includes(slug)) {
      return "This subdomain is reserved and cannot be used";
    }
    
    return null;
  };

  const checkSubdomainAvailability = async (slug: string) => {
    if (!slug || validateSubdomain(slug)) return;
    
    try {
      const { data, error } = await supabase
        .from('tenants')
        .select('id')
        .eq('slug', slug.toLowerCase())
        .maybeSingle();
      
      if (error) throw error;
      
      setSubdomainAvailable(data === null);
    } catch (error) {
      console.error('Error checking subdomain availability:', error);
      setSubdomainAvailable(null);
    }
  };

  const handleSubdomainChange = (value: string) => {
    const slug = value.toLowerCase().trim();
    setSubdomain(slug);
    
    const error = validateSubdomain(slug);
    setSubdomainError(error || "");
    setSubdomainAvailable(null);
    
    if (!error && slug) {
      // Debounce the availability check
      const timeoutId = setTimeout(() => checkSubdomainAvailability(slug), 500);
      return () => clearTimeout(timeoutId);
    }
  };

  const handleNewSignIn = async () => {
      sessionStorage.setItem('company_login_pending', 'true');
    let preserveExistingSessionOnFailure = false;
    try {
      setLoading(true);
      
      sessionStorage.setItem('requested_tenant_slug', companyId.trim().toLowerCase());
      // Keep the shared login when the same person opens the main portal beside
      // an outlet/factory portal. A different identity still needs a clean
      // browser auth context because Supabase Auth storage is shared by tabs.
      const { data: current } = await supabase.auth.getSession();
      const requestedEmail = employeeId.toLowerCase().trim();
      preserveExistingSessionOnFailure = current.session?.user?.email?.toLowerCase() === requestedEmail;
      if (current.session?.user?.email?.toLowerCase() !== requestedEmail) {
        if (current.session) await supabase.auth.signOut({ scope: 'local' });
        clearOutletContext();
      }
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email: employeeId.toLowerCase().trim(),
        password
      });
      
      if (error) throw error;

      const requestedTenantSlug = companyId.trim().toLowerCase();
      const { data: memberships, error: membershipError } = await supabase
        .from('memberships')
        .select('status, tenant:tenants!inner(slug, status)')
        .eq('user_id', data.user.id)
        .eq('status', 'active');
      const hasRequestedTenant = !membershipError && (memberships || []).some(
        (membership: any) => membership.tenant?.slug === requestedTenantSlug && membership.tenant?.status === 'active',
      );

      if (!hasRequestedTenant) {
        if (!preserveExistingSessionOnFailure) {
          await supabase.auth.signOut({ scope: 'local' });
        }
        throw new Error('Invalid Company ID, email, or password.');
      }

      localStorage.setItem('selectedTenantSlug', requestedTenantSlug);
      sessionStorage.removeItem('requested_tenant_slug');
      sessionStorage.removeItem('company_login_pending');
      navigate('/dashboard', { replace: true });
    } catch (err: any) {
      sessionStorage.removeItem('requested_tenant_slug');
      const msg = err?.message?.toLowerCase?.() ?? "";
      if (msg.includes("confirm") && employeeId) {
        try {
          const { error: resendErr } = await supabase.auth.resend({
            type: 'signup',
            email: employeeId.toLowerCase().trim(),
            options: { emailRedirectTo: redirectTo }
          });
          if (!resendErr) {
            toast({ title: "Email not confirmed", description: "We've re-sent your confirmation link." });
            return;
          }
        } catch {}
      }
      toast({ title: "Sign in failed", description: err.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
      sessionStorage.removeItem('company_login_pending');
  };


  const businessTypes = [
    { value: "food_beverage", label: "Food & Beverage" },
    { value: "retail_delivery", label: "Retail Delivery" },
    { value: "healthcare", label: "Healthcare" },
    { value: "field_service", label: "Field Service" },
    { value: "logistics", label: "Logistics" },
    { value: "pharmaceuticals", label: "Pharmaceuticals" },
    { value: "automotive", label: "Automotive" },
    { value: "real_estate", label: "Real Estate" },
    { value: "cleaning_services", label: "Cleaning Services" },
    { value: "home_services", label: "Home Services" },
    { value: "courier_delivery", label: "Courier Delivery" },
    { value: "other", label: "Other" },
  ];

  const handleEmailSignUp = async () => {
    try {
      setLoading(true);
      
      // CRITICAL: Clear any existing session before signup to prevent session leakage
      await supabase.auth.signOut({ scope: 'local' });
      clearOutletContext();
      
      // 1. Required fields present (do NOT trim passwords)
      if (!email.trim() || !firstName.trim() || !lastName.trim() || !phoneNumber.trim() || 
          !businessName.trim() || !legalName.trim() || !businessType || !businessCountry || !subdomain.trim() ||
          password.length === 0 || confirmPassword.length === 0) {
        toast({ title: "Missing required fields", description: "Please fill in all required fields.", variant: "destructive" });
        return;
      }
      
      // 2. Company ID validity & availability
      const slugError = validateSubdomain(subdomain);
      if (slugError) {
        toast({ title: "Invalid Company ID", description: slugError, variant: "destructive" });
        return;
      }
      
      if (subdomainAvailable === false) {
        toast({ title: "Company ID unavailable", description: "This Company ID is already taken. Please choose another.", variant: "destructive" });
        return;
      }
      
      // 3. Password strength (delegated to Supabase auth settings — placeholder for future explicit rules)
      
      // 4. Password confirmation (strict equality, no trimming)
      if (password !== confirmPassword) {
        toast({ title: "Passwords don't match", description: "Please make sure both password fields are identical.", variant: "destructive" });
        return;
      }
      
      // 5. Normalize Company ID and call Supabase
      const normalizedSlug = subdomain.trim().toLowerCase();
      const selectedCountry = countries.find(c => c.code === businessCountry);
      
      const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: { 
          emailRedirectTo: redirectTo,
          data: {
            first_name: firstName,
            last_name: lastName,
            phone_country_code: phoneCountryCode,
            phone_number: phoneNumber,
            company_name: businessName,
            legal_name: legalName,
            business_type: businessType,
            country: businessCountry,
            currency: currency,
            tenant_slug: normalizedSlug,
            timezone: selectedCountry?.timezone || "UTC"
          }
        },
      });
      if (signUpError) throw signUpError;

      // Detect Supabase's silent "user_repeated_signup": user is returned but identities array is empty.
      // Show a neutral message that does not disclose whether the email exists.
      const identities = (signUpData?.user as any)?.identities;
      if (Array.isArray(identities) && identities.length === 0) {
        toast({
          title: "Check your email",
          description:
            "If this is a new account, we've sent a confirmation link. If you already have an account, please sign in or reset your password.",
        });
        setConfirmPassword("");
        setShowConfirmPassword(false);
        setTab("signin");
        setEmployeeId(email);
        return;
      }

      toast({ title: "Check your inbox", description: "We sent a confirmation email." });
      // Reset confirm password on successful signup
      setConfirmPassword("");
      setShowConfirmPassword(false);
      setTab("signin");
    } catch (err: any) {
      const msg = err?.message?.toLowerCase?.() ?? "";
      const code = (err as any)?.code ?? "";
      
      // Authoritative uniqueness conflict on tenant_slug
      const isSlugConflict =
        code === "23505" ||
        msg.includes("tenant_slug") ||
        (msg.includes("duplicate key") && msg.includes("slug")) ||
        (msg.includes("already exists") && msg.includes("slug"));
      
      if (isSlugConflict) {
        setSubdomainAvailable(false);
        toast({
          title: "Company ID unavailable",
          description: "That Company ID was just taken. Please choose another.",
          variant: "destructive",
        });
        setTimeout(() => companyIdInputRef.current?.focus(), 0);
        return;
      }
      
      // Duplicate/repeated signup — neutral message, no existence disclosure.
      const looksDuplicate =
        msg.includes("already") || msg.includes("registered") || msg.includes("exists") || msg.includes("repeated");

      if (looksDuplicate) {
        toast({
          title: "Check your email",
          description:
            "If this is a new account, we've sent a confirmation link. If you already have an account, please sign in or reset your password.",
        });
        setTab("signin");
        setEmployeeId(email);
        return;
      }

      toast({ title: "Sign up failed", description: err.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleResendConfirmation = async () => {
    try {
      setResendLoading(true);
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: resendEmail,
        options: {
          emailRedirectTo: redirectTo
        }
      });
      if (error) throw error;
      toast({ 
        title: "Confirmation email sent", 
        description: "Check your inbox for the new confirmation link." 
      });
      setResendEmail("");
    } catch (err: any) {
      toast({ 
        title: "Failed to resend email", 
        description: err.message, 
        variant: "destructive" 
      });
    } finally {
      setResendLoading(false);
    }
  };

  const handleGoogle = async () => {
    try {
      setLoading(true);
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: { redirectTo },
      });
      if (error) throw error;
    } catch (err: any) {
      toast({ title: "Google sign-in failed", description: err.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!forgotPasswordEmail) {
      toast({ 
        title: "Email required", 
        description: "Please enter your email address",
        variant: "destructive" 
      });
      return;
    }

    try {
      setForgotPasswordLoading(true);
      const { error } = await supabase.auth.resetPasswordForEmail(
        forgotPasswordEmail.toLowerCase().trim(),
        {
          redirectTo: `${window.location.origin}/change-password`
        }
      );
      
      if (error) throw error;
      
      toast({ 
        title: "Reset email sent", 
        description: "Check your inbox for the password reset link" 
      });
      
      setShowForgotPassword(false);
      setForgotPasswordEmail("");
    } catch (err: any) {
      toast({ 
        title: "Failed to send reset email", 
        description: err.message, 
        variant: "destructive" 
      });
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-hero relative overflow-hidden">
      <div className="absolute inset-0 bg-black/20 pointer-events-none select-none" aria-hidden="true"></div>
      
      {/* Header */}
      <header className="relative z-10 p-6">
        <Link
          to="/"
          className="inline-flex items-center hover:opacity-90 transition-opacity"
          onClick={(e) => {
            if (e.shiftKey) {
              e.preventDefault();
              navigate("/super-admin/login");
            }
          }}
        >
          <LysaraLogo imgClassName="h-8 w-auto" invertOnDark />
        </Link>
      </header>

      <div className="flex items-center justify-center px-4 relative z-10" style={{ minHeight: 'calc(100vh - 100px)' }}>
        <div className="max-w-6xl w-full">
          {/* Persistent Error Alert from sessionStorage */}
          {persistentError && (
            <div className="mb-6 max-w-md mx-auto">
              <Alert variant="destructive" className="border-2">
                <AlertCircle className="h-5 w-5" />
                <AlertDescription className="ml-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <div className="font-semibold mb-1">Access Denied</div>
                      {persistentError.type === 'unauthorized_device' && (
                        <p className="text-sm">
                          This device is registered to <strong>{persistentError.outlet_name || 'another outlet'}</strong> 
                          {' '}but you are not a member of this organization. 
                          Please use your assigned device or contact your administrator.
                        </p>
                      )}
                      {persistentError.type === 'not_assigned_to_outlet' && (
                        <p className="text-sm">
                          You are not assigned to <strong>{persistentError.outlet_name || 'this outlet'}</strong>. 
                          Please contact your administrator to request access.
                        </p>
                      )}
                      {persistentError.type === 'not_outlet_owner' && (
                        <p className="text-sm">
                          This device is bound to an outlet you do not own. 
                          Please contact your administrator.
                        </p>
                      )}
                      {persistentError.type === 'unauthorized_role' && (
                        <p className="text-sm">
                          Your role is not authorized to use outlet devices. 
                          Only outlet staff can access this device.
                        </p>
                      )}
                      {persistentError.type === 'authorization_check_failed' && (
                        <p className="text-sm">
                          Unable to verify your access permissions for <strong>{persistentError.outlet_name || 'this outlet'}</strong>. 
                          Please try again or contact support.
                        </p>
                      )}
                      {persistentError.device_id && (
                        <p className="text-xs text-muted-foreground mt-2">
                          Device ID: ...{persistentError.device_id.slice(-6)}
                        </p>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={clearPersistentError}
                      className="h-6 w-6 p-0"
                    >
                      <XCircle className="h-4 w-4" />
                    </Button>
                  </div>
                </AlertDescription>
              </Alert>
            </div>
          )}
          
          {/* URL-based Error Alerts */}
          {errorType && !persistentError && (
            <div className="mb-6 max-w-md mx-auto">
              {errorType === 'unauthorized_device' && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    This device is registered to a different organization. Please use your assigned device or contact your administrator.
                  </AlertDescription>
                </Alert>
              )}
              
              {errorType === 'not_assigned_to_outlet' && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    You are not assigned to this outlet. Please contact your administrator to request access.
                  </AlertDescription>
                </Alert>
              )}
              
              {errorType === 'not_outlet_owner' && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    This device is bound to an outlet you do not own. Please contact your administrator.
                  </AlertDescription>
                </Alert>
              )}
              
              {errorType === 'unauthorized_role' && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Your role is not authorized to use outlet devices. Only outlet staff can access this device.
                  </AlertDescription>
                </Alert>
              )}
              
              {errorType === 'authorization_check_failed' && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Unable to verify your access permissions. Please try again or contact support.
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          
          {/* Left Panel - Branding */}
          <div className="hidden lg:flex flex-col justify-center text-white space-y-8 min-h-[600px]">
            <div>
              <h1 className="text-4xl font-bold mb-4">
                Complete Your Account & 
                <span className="block text-blue-100">Business Setup in One Step</span>
              </h1>
              <p className="text-xl text-blue-100 leading-relaxed">
                Set up your business profile and get started with industry-specific templates.
              </p>
            </div>
            
            <div className="space-y-6">
              <div className="flex items-start gap-3">
                <CheckCircle className="h-6 w-6 text-green-300 mt-1" />
                <div>
                  <h3 className="font-semibold mb-1">Works for Any Industry</h3>
                  <p className="text-blue-100 text-sm">
                    From retail to healthcare, logistics to field service - one platform for all your needs.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <CheckCircle className="h-6 w-6 text-green-300 mt-1" />
                <div>
                  <h3 className="font-semibold mb-1">Mobile-First Design</h3>
                  <p className="text-blue-100 text-sm">
                    Native mobile experience with offline capabilities for your field teams.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <CheckCircle className="h-6 w-6 text-green-300 mt-1" />
                <div>
                  <h3 className="font-semibold mb-1">Enterprise Ready</h3>
                  <p className="text-blue-100 text-sm">
                    Multi-tenant architecture with enterprise-grade security and compliance.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
              <p className="text-blue-100 mb-2 text-sm">
                "Lysara transformed our operations. The flexibility to customize workflows has been game-changing."
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  <span className="text-white font-semibold">SJ</span>
                </div>
                <div>
                  <p className="text-white font-medium text-sm">Sarah Johnson</p>
                  <p className="text-blue-200 text-xs">Operations Manager, RetailCorp</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Panel - Auth Form */}
          <div className="flex items-start justify-center pt-8 lg:pt-0">
            <Card className="w-full max-w-md bg-white/95 backdrop-blur-sm border-white/20 shadow-brand">
              <CardHeader className="text-center">
                <LysaraLogo className="justify-center mb-4" imgClassName="h-12 w-auto" />
                <CardTitle className="text-2xl text-primary">Welcome to Lysara</CardTitle>
                <CardDescription>Sign in to your account or create a new one</CardDescription>
              </CardHeader>
              <CardContent>
                
                <Tabs value={tab} onValueChange={(v) => {
                  setTab(v as any);
                  if (v !== "signup") {
                    setConfirmPassword("");
                    setShowConfirmPassword(false);
                  }
                }}>
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="signin">Sign In</TabsTrigger>
                    <TabsTrigger value="signup">Sign Up</TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="signin" className="mt-4">
                    <div className="space-y-4">
                      {isReturningUser && tenantName && (
                        <Alert className="bg-blue-50 border-blue-200">
                          <AlertCircle className="h-4 w-4 text-blue-600" />
                          <AlertDescription className="text-blue-800">
                            <strong>Welcome back!</strong> You're an existing Lysara user. Please log in with your existing password to access <strong>{tenantName}</strong>.
                          </AlertDescription>
                        </Alert>
                      )}
                      <div className="space-y-2">
                        <Label htmlFor="company-id">Company ID</Label>
                        <Input
                          id="company-id"
                          type="text"
                          placeholder="Company ID"
                          value={companyId}
                          onChange={(e) => setCompanyId(e.target.value)}
                        />
                        <p className="text-xs text-muted-foreground">
                          Your company's unique identifier (e.g., "lysara")
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="employee-id">Employee ID</Label>
                        <Input
                          id="employee-id"
                          type="email"
                          placeholder="john.doe@company.com"
                          value={employeeId}
                          onChange={(e) => setEmployeeId(e.target.value)}
                        />
                        <p className="text-xs text-muted-foreground">
                          Your work email address
                        </p>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label htmlFor="password-new">Password</Label>
                          <Button
                            variant="link"
                            size="sm"
                            className="text-xs p-0 h-auto"
                            onClick={() => {
                              setShowForgotPassword(true);
                              setForgotPasswordEmail(employeeId);
                            }}
                          >
                            Forgot Password?
                          </Button>
                        </div>
                        <Input
                          id="password-new"
                          type="password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          onKeyDown={(e) => e.key === "Enter" && handleNewSignIn()}
                        />
                      </div>
                      <Button className="w-full" onClick={handleNewSignIn} disabled={loading || !companyId.trim() || !employeeId.trim() || !password.trim()}>
                        {loading ? "Signing in..." : "Sign In"}
                      </Button>

                    </div>
                  </TabsContent>
                  
                  <TabsContent value="signup" className="mt-4">
                    <div className="space-y-6">
                      {/* Personal Information Section */}
                      <div className="space-y-4">
                        <h3 className="font-semibold text-foreground">Personal Information</h3>
                        
                        <div className="space-y-2">
                          <Label htmlFor="email-up">Email *</Label>
                          <Input 
                            id="email-up" 
                            type="email" 
                            placeholder="john@company.com" 
                            value={email} 
                            onChange={(e) => setEmail(e.target.value)} 
                          />
                        </div>
                        
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-2">
                            <Label htmlFor="first-name">First Name *</Label>
                            <Input 
                              id="first-name" 
                              type="text" 
                              placeholder="John" 
                              value={firstName} 
                              onChange={(e) => setFirstName(e.target.value)} 
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="last-name">Last Name *</Label>
                            <Input 
                              id="last-name" 
                              type="text" 
                              placeholder="Doe" 
                              value={lastName} 
                              onChange={(e) => setLastName(e.target.value)} 
                            />
                          </div>
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="password-up">Create Password *</Label>
                          <div className="relative">
                            <Input 
                              id="password-up" 
                              type={showPassword ? "text" : "password"} 
                              value={password} 
                              onChange={(e) => setPassword(e.target.value)} 
                              placeholder="Create a secure password"
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 p-0"
                              onClick={() => setShowPassword(!showPassword)}
                            >
                              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </Button>
                          </div>
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="confirm-password-up">Confirm Password *</Label>
                          <div className="relative">
                            <Input 
                              id="confirm-password-up" 
                              type={showConfirmPassword ? "text" : "password"} 
                              value={confirmPassword} 
                              onChange={(e) => setConfirmPassword(e.target.value)} 
                              placeholder="Re-enter your password"
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 p-0"
                              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            >
                              {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </Button>
                          </div>
                          {confirmPassword.length > 0 && password !== confirmPassword && (
                            <p className="text-xs text-red-500">Passwords don't match</p>
                          )}
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="phone-number">Phone Number *</Label>
                          <PhoneInput
                            value={phoneNumber}
                            countryCode={phoneCountryCode}
                            onValueChange={setPhoneNumber}
                            onCountryCodeChange={setPhoneCountryCode}
                            placeholder="Enter your phone number"
                          />
                        </div>
                      </div>

                      {/* Business Information Section */}
                      <div className="space-y-4 pt-4 border-t">
                        <h3 className="font-semibold text-foreground">Business Information</h3>
                        
                        <div className="space-y-2">
                          <Label htmlFor="business-name">Business Name *</Label>
                          <Input 
                            id="business-name" 
                            type="text" 
                            placeholder="Acme Corporation" 
                            value={businessName} 
                            onChange={(e) => setBusinessName(e.target.value)} 
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="legal-name">Company Legal Name (for tax) *</Label>
                          <Input
                            id="legal-name"
                            type="text"
                            placeholder="Acme Corporation Pte Ltd"
                            value={legalName}
                            onChange={(e) => setLegalName(e.target.value)}
                          />
                          <p className="text-xs text-muted-foreground">
                            The registered legal name used on invoices and receipts.
                          </p>
                        </div>
                        
                        
                        <div className="space-y-2">
                          <Label htmlFor="business-type">Business Type *</Label>
                          <Select value={businessType} onValueChange={setBusinessType}>
                            <SelectTrigger>
                              <SelectValue placeholder="Select your business type" />
                            </SelectTrigger>
                            <SelectContent>
                              {businessTypes.map((type) => (
                                <SelectItem key={type.value} value={type.value}>
                                  {type.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Alert className="bg-blue-50 border-blue-200">
                            <AlertCircle className="h-4 w-4 text-blue-600" />
                            <AlertDescription className="text-blue-700">
                              Business type affects system configuration and cannot be changed later.
                            </AlertDescription>
                          </Alert>
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="business-country">Business Based Country *</Label>
                          <CountrySelect
                            value={businessCountry}
                            onValueChange={setBusinessCountry}
                            onCurrencyChange={setCurrency}
                            placeholder="Select your business country"
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="currency">Currency</Label>
                          <Input 
                            id="currency" 
                            type="text" 
                            value={currency} 
                            readOnly 
                            className="bg-muted"
                            placeholder="Will be set based on country"
                          />
                          <Alert className="bg-blue-50 border-blue-200">
                            <AlertCircle className="h-4 w-4 text-blue-600" />
                            <AlertDescription className="text-blue-700">
                              Country selection affects currency and cannot be changed later.
                            </AlertDescription>
                          </Alert>
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="subdomain">Company ID *</Label>
                          <Input 
                            id="subdomain" 
                            type="text" 
                            placeholder="acme" 
                            value={subdomain} 
                            ref={companyIdInputRef}
                            onChange={(e) => handleSubdomainChange(e.target.value)} 
                            className={subdomainError ? "border-red-500" : subdomainAvailable === true ? "border-green-500" : ""}
                          />
                          {subdomainError && <p className="text-xs text-red-500">{subdomainError}</p>}
                          {subdomainAvailable === true && <p className="text-xs text-green-600">Company ID available!</p>}
                          {subdomainAvailable === false && <p className="text-xs text-red-500">Company ID already taken</p>}
                          <p className="text-xs text-muted-foreground">
                            Your organization's unique Company ID. You'll use this to sign in.
                          </p>
                        </div>
                      </div>

                      <Button 
                        className="w-full" 
                        onClick={handleEmailSignUp} 
                        disabled={loading || !email.trim() || password.length === 0 || confirmPassword.length === 0 || password !== confirmPassword || !firstName.trim() || !lastName.trim() || !phoneNumber.trim() || !businessName.trim() || !legalName.trim() || !businessType || !businessCountry || !subdomain.trim()}
                      >
                        {loading ? "Creating account..." : "Create Account"}
                      </Button>
                    </div>
                  </TabsContent>
                </Tabs>
                
                {tab === "signin" && (
                  <>
                    <Separator className="my-6" />
                    
                    <Button 
                      variant="outline" 
                      className="w-full" 
                      onClick={handleGoogle} 
                      disabled={loading}
                    >
                      <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                        <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                      Continue with Google
                    </Button>
                  </>
                )}
                
                <div className="mt-6 space-y-4">
                  <h3 className="font-medium text-sm">Need to resend confirmation email?</h3>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Enter your email"
                      value={resendEmail}
                      onChange={(e) => setResendEmail(e.target.value)}
                      className="flex-1"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleResendConfirmation}
                      disabled={resendLoading || !resendEmail.trim()}
                    >
                      {resendLoading ? "Sending..." : "Resend"}
                    </Button>
                  </div>
                </div>
              </CardContent>
              
              {/* Mobile branding footer */}
              <div className="lg:hidden bg-muted/30 p-6 rounded-b-lg">
                <p className="text-xs text-muted-foreground text-center">
                  Join thousands of businesses using Lysara to streamline their operations
                </p>
              </div>
            </Card>
          </div>
          </div>
        </div>
      </div>
      
      {/* Forgot Password Dialog */}
      <Dialog open={showForgotPassword} onOpenChange={setShowForgotPassword}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>
              Enter your email address and we'll send you a link to reset your password.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="forgot-email">Email Address</Label>
              <Input
                id="forgot-email"
                type="email"
                value={forgotPasswordEmail}
                onChange={(e) => setForgotPasswordEmail(e.target.value)}
                placeholder="your.email@company.com"
                onKeyDown={(e) => e.key === "Enter" && handleForgotPassword()}
              />
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowForgotPassword(false);
                setForgotPasswordEmail("");
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleForgotPassword} 
              disabled={forgotPasswordLoading || !forgotPasswordEmail}
            >
              {forgotPasswordLoading ? "Sending..." : "Send Reset Link"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
